import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { Reveal, MediaPlaceholder, CountUp, EASE, waLink, useCycleIndex, fbLead } from "./primitives";

/* ---------- The Reframe (pause statement) ---------- */
export function Reframe() {
  const ref = useRef(null);
  return (
    <section id="the-house" ref={ref} className="py-32 md:py-52 px-6" style={{ background: "var(--ivory)", color: "var(--forest)" }} data-testid="reframe-section">
      <div className="mx-auto max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-15% 0px" }}
          transition={{ duration: 1, ease: EASE }}
        >
          <span className="eyebrow" style={{ color: "var(--brass)" }}>The Reframe</span>
          <h2 className="font-heading font-light leading-[1.08] tracking-[-0.01em] mt-8 text-[2rem] sm:text-[2.9rem] lg:text-[3.6rem]">
            This is not a service. It is hospitality extended personally. By us, to you.
          </h2>
        </motion.div>

        <div className="mt-14 flex flex-col items-center gap-1">
          {["A service responds. Hospitality anticipates.", "A service is requested. Hospitality is already there.", "A service ends. Hospitality remembers."].map((line, i) => (
            <Reveal key={i} delay={0.12 * i}>
              <p className="font-body italic text-xl md:text-2xl leading-tight" style={{ color: "var(--olive)" }}>{line}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- The Circle Stays Small (50) ---------- */
export function CircleFifty() {
  return (
    <section id="membership" className="lux relative py-28 md:py-44 px-6 md:px-10 overflow-hidden" data-testid="fifty-section">
      <img src="/media/fifty-bg.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" aria-hidden style={{ background: "linear-gradient(to right, rgba(23,64,42,0.94) 0%, rgba(23,64,42,0.82) 50%, rgba(23,64,42,0.72) 100%)" }} />
      <div className="relative z-10 mx-auto max-w-[1500px] grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <Reveal>
          <div className="leading-none">
            <span className="eyebrow" style={{ color: "var(--brass)" }}>The Founding Circle</span>
            <div className="font-heading font-light tracking-[-0.04em] text-[10rem] sm:text-[13rem] lg:text-[16rem] leading-[0.8] mt-4" style={{ color: "var(--ivory)" }}>
              <CountUp to={50} />
            </div>
            <p className="eyebrow mt-5" style={{ color: "rgba(245,238,223,0.55)" }}>
              Founding Members &middot; Across India
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="max-w-md">
            <p className="font-body italic text-2xl md:text-3xl leading-snug" style={{ color: "var(--ivory)" }}>
              Fifty founding members. The first invited, and the ones who come to define the circle.
            </p>
            <div className="my-8" style={{ height: 1, background: "rgba(120,132,107,0.35)" }} />
            <p className="font-body text-xl leading-relaxed" style={{ color: "rgba(245,238,223,0.68)" }}>
              Membership is by referral. There is no public application. Once the founding fifty is complete, the circle quietly closes. It opens for you, or it does not open at all.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- The First Gesture (split screen) ---------- */
export function FirstGesture() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <section id="first-gesture" ref={ref} className="lux relative lg:grid lg:grid-cols-2 items-stretch overflow-hidden" style={{ backgroundColor: "var(--olive)" }} data-testid="first-gesture-section">
      {/* Image: full-bleed background under 1024px, static left column on desktop */}
      <div className="absolute inset-0 lg:relative lg:inset-auto min-h-[88vh] lg:min-h-screen overflow-hidden lg:p-8">
        <motion.div style={{ y }} className="absolute inset-0">
          <div className="relative h-full w-full overflow-hidden lg:border lg:border-[#78846B]/30">
            <img src="/media/jet.webp" alt="Private jet cabin" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </motion.div>
      </div>

      {/* Legibility scrim for the overlaid text (mobile / iPad only) */}
      <div className="absolute inset-0 lg:hidden" aria-hidden style={{ background: "linear-gradient(to right, rgba(28,46,35,0.94) 0%, rgba(28,46,35,0.82) 55%, rgba(28,46,35,0.62) 100%)" }} />
      <div className="absolute inset-0 lg:hidden" aria-hidden style={{ background: "rgba(28,46,35,0.34)" }} />

      {/* Text: overlaid on mobile/iPad, right column on desktop */}
      <div className="relative z-10 flex items-center px-6 lg:px-16 py-28 lg:py-40 min-h-[88vh] lg:min-h-0">
        <div className="max-w-xl" style={{ textShadow: "0 1px 14px rgba(20,34,26,0.5)" }}>
          <Reveal>
            <span className="eyebrow" style={{ color: "var(--brass)" }}>The First Gesture</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-heading font-light leading-[1.05] tracking-[-0.01em] mt-6 text-[2rem] md:text-[3rem]" style={{ color: "var(--ivory)" }}>
              Welcomed by the founder, not an email.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-6 font-body text-xl md:text-2xl leading-relaxed" style={{ color: "rgba(245,238,223,0.9)" }}>
              <p>When you are accepted, I fly to you. Wherever you are.</p>
              <p>I take you to dinner. There is no paperwork at the table, no pitch. Only a conversation, and the beginning of a relationship that stays discreet for as long as it lasts.</p>
              <p>This is how I spend the first evening. In person. Because that is the whole point.</p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10">
              <p className="font-body italic text-2xl" style={{ color: "var(--ivory)" }}>Samaksh Bhasin</p>
              <p className="eyebrow mt-1" style={{ color: "var(--sage)" }}>Founder, Hitakari House</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- Mobile scroll-driven parallax gallery (indulge-style) ---------- */
function MobileScrollGallery({ items, startIndex, direction }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const flip = direction === "right" ? -1 : 1;
  const yA = useTransform(scrollYProgress, [0, 1], [`${44 * flip}px`, `${-44 * flip}px`]);
  const yB = useTransform(scrollYProgress, [0, 1], [`${-44 * flip}px`, `${44 * flip}px`]);
  const cols = [items.filter((_, i) => i % 2 === 0), items.filter((_, i) => i % 2 === 1)];
  const ys = [yA, yB];
  const Tile = ({ it, n }) => (
    <div className="relative overflow-hidden aspect-[3/4]" style={{ boxShadow: "0 26px 55px -22px rgba(0,0,0,0.7)" }}>
      <img src={it.src} alt={it.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,34,26,0.95) 0%, rgba(20,34,26,0.3) 46%, rgba(20,34,26,0.03) 72%)" }} />
      <div className="absolute inset-x-0 bottom-0 p-4">
        <span className="font-heading font-light text-[0.65rem] tabular-nums" style={{ color: "var(--brass)" }}>{String(n).padStart(2, "0")}</span>
        <h3 className="font-heading font-light text-lg mt-1 leading-tight" style={{ color: "var(--ivory)" }}>{it.title}</h3>
      </div>
    </div>
  );
  return (
    <div ref={ref} className="grid grid-cols-2 gap-0">
      {cols.map((col, c) => (
        <motion.div key={c} style={{ y: ys[c] }} className="flex flex-col gap-0">
          {col.map((it) => (
            <Tile key={it.title} it={it} n={startIndex + items.indexOf(it)} />
          ))}
        </motion.div>
      ))}
    </div>
  );
}

/* ---------- Auto-sliding concierge carousel ---------- */
function ConciergeCarousel({ id, eyebrow, titleMain, titleAccent, subtitle, items, bg, direction = "left", startIndex = 1, glow = "rgba(16,28,21,0.85)" }) {
  return (
    <section id={id} className="lux py-28 md:py-40 overflow-hidden" style={{ backgroundColor: bg }} data-testid={`${id}-section`}>
      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
        <div
          aria-hidden
          className="absolute pointer-events-none left-0 -top-14 h-[360px] w-[720px] max-w-full rounded-full blur-[90px]"
          style={{ background: `radial-gradient(closest-side, ${glow}, transparent)` }}
        />
        <div className="relative">
          <Reveal>
            <span className="eyebrow" style={{ color: "var(--brass)" }}>{eyebrow}</span>
            <h2 className="font-heading font-light leading-[1.08] tracking-[-0.01em] mt-5 max-w-3xl text-[2rem] md:text-[3rem]" style={{ color: "var(--ivory)" }}>
              {titleMain}{" "}
              <span className="font-body italic" style={{ color: "var(--brass)" }}>{titleAccent}</span>
            </h2>
            {subtitle && (
              <p className="font-body italic text-xl md:text-2xl mt-5 max-w-xl" style={{ color: "rgba(245,238,223,0.6)" }}>{subtitle}</p>
            )}
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.15} className="mt-14 md:mt-16 hidden md:block">
        <div aria-hidden="true"><Marquee speed={42} direction={direction} pauseOnHover autoFill gradient gradientColor={bg} gradientWidth={110}>
          {items.map((it, i) => (
            <div key={it.title} className="group relative mx-3 md:mx-4 w-[268px] md:w-[338px]" data-testid={`${id}-card-${i}`}>
              <div className="relative overflow-hidden aspect-[3/4]" style={{ boxShadow: "0 26px 55px -22px rgba(0,0,0,0.7)" }}>
                <img
                  src={it.src}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-[1.06]"
                  style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,34,26,0.96) 0%, rgba(20,34,26,0.32) 48%, rgba(20,34,26,0.04) 72%)" }} />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="font-heading font-light text-xs tabular-nums" style={{ color: "var(--brass)" }}>
                    {String(startIndex + i).padStart(2, "0")}
                  </span>
                  <div className="font-heading font-light text-xl md:text-2xl mt-2" style={{ color: "var(--ivory)" }}>{it.title}</div>
                  <div
                    className="font-body text-lg leading-snug max-h-0 opacity-0 group-hover:max-h-48 group-hover:opacity-100 group-hover:mt-3 transition-[max-height,opacity,margin] duration-500"
                    style={{ color: "rgba(245,238,223,0.82)", transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
                  >
                    {it.body}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Marquee></div>
      </Reveal>

      {/* Screen-reader / crawler accessible list (visually hidden, rendered once) */}
      <ul className="sr-only">
        {items.map((it, i) => (
          <li key={`sr-${it.title}`}>
            <h3>{String(startIndex + i).padStart(2, "0")} — {it.title}</h3>
            <p>{it.body}</p>
          </li>
        ))}
      </ul>

      <div className="md:hidden mt-12">
        <MobileScrollGallery items={items} startIndex={startIndex} direction={direction} />
      </div>
    </section>
  );
}

export function HouseExtends() {
  const items = [
    { title: "Chauffeur & Personal Travel", src: "/media/hx-chauffeur.png", body: "A car that knows the route and the hour. You arrive. Nothing in between asks anything of you." },
    { title: "Private Aviation", src: "/media/hx-aviation.png", body: "Wheels up on short notice. Helicopter or jet, the sky arranged around your day." },
    { title: "Private Dining", src: "/media/hx-dining.png", body: "A table held quietly. Chef briefed on what you love. The evening shaped around the company you keep." },
    { title: "Networking & Introductions", src: "/media/hx-networking.png", body: "The right room, the right people. Introductions made with intention, never by accident." },
    { title: "Itinerary & Travel Planning", src: "/media/hx-itinerary.png", body: "A journey composed in full, door to door. You carry nothing but yourself. The rest is anticipated." },
    { title: "Rare Drives & Motoring", src: "/media/hx-rare.png", body: "The car you always wanted to drive, waiting where you want it. For an hour or a season." },
    { title: "Personal Shopping & Sourcing", src: "/media/hx-shopping.png", body: "The piece before it reaches the floor. Found, held, and brought quietly to you." },
    { title: "Private Events & Access", src: "/media/hx-events.png", body: "Doors that do not open for most, opened for an evening. Access held quietly in your name." },
  ];
  return (
    <ConciergeCarousel
      id="house-extends"
      eyebrow="What the House Extends"
      titleMain="What you'll ask,"
      titleAccent="before you ask."
      subtitle="And a great deal more, arranged quietly in the background."
      bg="#17402A"
      direction="left"
      startIndex={1}
      glow="rgba(10,18,13,0.9)"
      items={items}
    />
  );
}

export function CareMemory() {
  const items = [
    { title: "Events & Heritage Spaces", src: "/media/heritage-hall.jpg", body: "Rooms with a history, opened for an evening. Gatherings held in places most never see." },
    { title: "Gifting & Occasion Tracking", src: "/media/gifting-shopping.jpg", body: "The dates that matter, kept for you. The right gesture arrives on its own, in your name." },
    { title: "Preferences Remembered", src: "/media/preference-dining.jpg", body: "Your table, your wine, the side of the room you prefer. Noted once, honoured always." },
    { title: "Sourcing & Quiet Coordination", src: "/media/download.jpeg", body: "The hard to find, found. The complicated, coordinated. All of it, without noise." },
    { title: "Milestones & Anniversaries", src: "/media/milestone.jpg", body: "The moments that matter to those you love, remembered on your behalf, in good time." },
    { title: "Discreet Errands", src: "/media/sr1.webp", body: "The small things handled quietly, so the day stays yours and nothing is left undone." },
    { title: "Household & Staff Coordination", src: "/media/member-promoters.jpg", body: "The house readied before you arrive. People briefed, details settled, calm restored." },
    { title: "Wellness & Recovery", src: "/media/wellness.jpg", body: "Rest arranged as carefully as any journey. The right people, the right hour, quietly." },
  ];
  return (
    <ConciergeCarousel
      id="care-memory"
      eyebrow="Care Has a Memory"
      titleMain="What matters to you is remembered,"
      titleAccent="so you never have to repeat."
      subtitle="Noted once, and never asked of you again."
      bg="#4A4A2E"
      direction="right"
      startIndex={9}
      glow="rgba(23,64,42,0.9)"
      items={items}
    />
  );
}

/* ---------- Special Requests (bespoke band) ---------- */
export function SpecialRequests() {
  return (
    <section id="special-requests" className="relative py-28 md:py-44 px-6 overflow-hidden" data-testid="special-requests-section">
      <img src="/media/special-collage.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" aria-hidden style={{ background: "rgba(28,46,35,0.6)" }} />
      <div className="absolute inset-0" aria-hidden style={{ background: "radial-gradient(120% 85% at 50% 50%, rgba(28,46,35,0.28), rgba(28,46,35,0.82))" }} />
      <div className="relative z-10 mx-auto max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-15% 0px" }}
          transition={{ duration: 1, ease: EASE }}
        >
          <span className="eyebrow" style={{ color: "var(--brass)" }}>Special Requests</span>
          <h2 className="font-heading font-light leading-[1.08] tracking-[-0.01em] mt-8 text-[2rem] sm:text-[2.8rem] lg:text-[3.4rem]" style={{ color: "var(--ivory)" }}>
            For things too special to be on a list.
          </h2>
        </motion.div>

        <Reveal delay={0.1}>
          <p className="font-body italic text-xl md:text-2xl mt-8 max-w-2xl mx-auto leading-relaxed" style={{ color: "rgba(245,238,223,0.75)" }}>
            The unusual, the last minute, the deeply personal. If it can be imagined, it can be asked. A single message is enough.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <a
            href={waLink("\nI have a special request.")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => fbLead("whatsapp")}
            className="inline-block mt-10 font-label text-[0.75rem] tracking-[0.24em] uppercase link-underline font-light"
            style={{ color: "var(--ivory)" }}
            data-testid="special-request-whatsapp-btn"
          >
            Make a Request
          </a>
        </Reveal>
      </div>
    </section>
  );
}


/* A single frame that cycles through category+photo pairs (title changes with photo) */
function CyclingTile({ entries, delay }) {
  const idx = useCycleIndex(entries.length, 4200, delay);
  return (
    <div className="group" data-testid="cycling-tile">
      <div className="relative overflow-hidden aspect-[3/4]" style={{ boxShadow: "0 26px 55px -22px rgba(0,0,0,0.7)" }}>
        <div className="relative h-full w-full transition-transform duration-[700ms] group-hover:scale-[1.05]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
          {entries.map((e, i) => (
            <img
              key={e.label}
              src={e.src}
              alt={i === idx ? e.label : ""}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1100ms]"
              style={{ opacity: i === idx ? 1 : 0, transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
            />
          ))}
        </div>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(20,34,26,0.92) 0%, rgba(20,34,26,0.25) 42%, transparent 68%)" }} />
        {entries.map((e, i) => (
          <p
            key={e.label}
            className="font-label uppercase font-light tracking-[0.24em] text-xs md:text-sm absolute left-4 right-4 bottom-4 transition-opacity duration-[700ms]"
            style={{ color: "var(--ivory)", opacity: i === idx ? 1 : 0 }}
          >
            {e.label}
          </p>
        ))}
      </div>
    </div>
  );
}


export function CircleProfile() {
  const fields = ["Promoters", "Founders", "Athletes", "Media", "Family Business Owners", "Legacy Families", "Celebrities", "Investors"];
  return (
    <section id="the-circle" className="py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--ivory)" }} data-testid="circle-profile-section">
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <div className="relative">
          <Reveal className="relative">
            <span className="eyebrow" style={{ color: "var(--brass)" }}>The Circle</span>
            <h2 className="font-heading font-light leading-[1.08] tracking-[-0.01em] mt-5 max-w-3xl text-[2rem] md:text-[3rem]" style={{ color: "var(--forest)" }}>
              A small circle,{" "}
              <span className="font-body italic" style={{ color: "var(--brass)" }}>drawn from every field that shapes India.</span>
            </h2>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mt-14">
        {[
            { delay: 0, entries: [
              { label: "Promoters", src: "/media/promoter-hakimi.jpg" },
              { label: "Family Business Owners", src: "/media/business-family.jpg" },
            ] },
            { delay: 1050, entries: [
              { label: "Founders", src: "/media/founder-gallery.jpg" },
              { label: "Legacy Families", src: "/media/legacy-family.jpg" },
            ] },
            { delay: 2100, entries: [
              { label: "Athletes", src: "/media/athletes.webp" },
              { label: "Celebrities", src: "/media/celebrity.jpg" },
            ] },
            { delay: 3150, entries: [
              { label: "Media", src: "/media/press-media.jpg" },
              { label: "Investors", src: "/media/investors-suit.jpg" },
            ] },
          ].map((tile, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <CyclingTile entries={tile.entries} delay={tile.delay} />
            </Reveal>
          ))}
        </div>

      <div className="my-10 md:my-14 select-none" aria-hidden style={{ borderTop: "1px solid rgba(23,64,42,0.2)", borderBottom: "1px solid rgba(23,64,42,0.2)" }}>
        <Marquee speed={26} autoFill pauseOnHover gradient gradientColor="#F5EEDF" gradientWidth={180} className="py-4">
          {fields.map((r, i) => (
            <span key={i} className="inline-flex items-center">
              <span className="font-label uppercase font-light text-sm md:text-base tracking-[0.3em]" style={{ color: "var(--forest)" }}>
                {r}
              </span>
              <span className="mx-8 md:mx-14 rotate-45" style={{ width: 5, height: 5, background: "var(--brass)", display: "inline-block" }} />
            </span>
          ))}
        </Marquee>
      </div>

      <div className="mx-auto max-w-[1500px] px-6 md:px-10 grid md:grid-cols-2 gap-12 items-end">
        <Reveal>
          <p className="font-body text-2xl md:text-3xl leading-relaxed" style={{ color: "var(--forest)" }}>
            Every member is hand picked. Not applied for, not sold to. Chosen.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="font-body text-xl leading-relaxed" style={{ color: "var(--olive)" }}>
            And every relationship stays discreet. Who belongs to the circle is known only within it. This privacy is not a feature. It is the foundation.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
